/**
 * `AIRealtimeBasePromptCatalog.ts` — port of Unity's `AI/AIRealtimeBasePromptCatalog.cs`.
 *
 * Loads the base system prompt and registers it on `agentPrompts`. The
 * deployment's PUBLISHED `base.md` wins; otherwise the copy that shipped in the
 * build at `/AI/prompt/base.md` (served from `public/`). There is no compiled-in
 * fallback — if neither is readable the base prompt stays empty and an error is
 * logged. Must be awaited before the realtime session connects so the first
 * prompt is complete.
 *
 * The cached load is RESETTABLE (`force`), unlike the original bare
 * `s_loadTask ??=`. That seam is what makes `base.md` publishable at all: without
 * it the first load of the session was permanent, so a newly published base
 * prompt could not take effect until a page reload.
 */

import { setBaseInstructionsOverride } from "./agentPrompts";
import type { AgentPromptSource } from "./IAIPromptStoreModel";

import { publicUrl } from "@core/publicUrl";
const K_BASE_PROMPT_PATH = publicUrl("AI/prompt/base.md");

/** The file name the published copy is keyed on. */
export const BASE_PROMPT_FILE_NAME = "base.md";

let s_loadTask: Promise<void> | null = null;
/** The text last installed, so the debug panel can render what the agent has. */
let s_loadedText = "";

/**
 * Idempotent cached load (mirrors Unity's `s_loadTask ??= LoadAsync()`), with a
 * `force` escape hatch for a publish or reset.
 */
export function ensureBasePromptLoadedAsync(
  prompts?: AgentPromptSource | null,
  force = false,
): Promise<void> {
  if (force) s_loadTask = null;
  return (s_loadTask ??= loadAsync(prompts));
}

/** The base prompt currently installed — published copy or shipped file. */
export function getLoadedBasePromptText(): string {
  return s_loadedText;
}

async function loadAsync(prompts?: AgentPromptSource | null): Promise<void> {
  const published = prompts?.getPromptText("base", BASE_PROMPT_FILE_NAME) ?? null;

  let markdown: string | null = published;
  if (markdown === null) {
    try {
      // `no-cache` so a reset re-reads `public/AI/**` from the server rather
      // than a memory-cached copy from earlier in the same session.
      const response = await fetch(K_BASE_PROMPT_PATH, { cache: "no-cache" });
      if (response.ok) {
        markdown = await response.text();
      }
    } catch (e) {
      console.error(`[AIRealtimeBasePromptCatalog] Failed to read ${K_BASE_PROMPT_PATH}:`, e);
    }
  }

  if (markdown && markdown.trim()) {
    s_loadedText = markdown;
    setBaseInstructionsOverride(markdown);
  } else {
    // base.md is the sole source of the base prompt; there is no compiled-in fallback.
    s_loadedText = "";
    setBaseInstructionsOverride(null);
    console.error(
      `[AIRealtimeBasePromptCatalog] Missing/empty ${K_BASE_PROMPT_PATH}; base prompt will be empty (no compiled-in fallback).`,
    );
  }
}
